// src/pages/SupportInbox.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
// Pantalla "Atención a Clientes" para staff. Layout 3 columnas tipo Intercom.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import socket from '../services/socket';
import { inboxList, inboxDetail, sendAgent, closeConv, reopenConv, escalateToTech, getBotStatus, setBotStatus,
         inboxMetrics, clientContext, takeConv, transferConv, snoozeConv, listSupportAgents } from '../services/supportApi';
import { Link } from 'react-router-dom';


// ── Iconos SVG inline ────────────────────────────────────────────────────────
const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    paperclip: <Icon><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.58 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" /></Icon>,
    send:      <Icon><path d="m22 2-7 20-4-9-9-4 20-7Z" /></Icon>,
    clock:     <Icon size={12}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
    user:      <Icon size={12}><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /></Icon>,
    sparkles:  <Icon size={14}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></Icon>,
    chevDown:  <Icon size={14}><path d="m6 9 6 6 6-6" /></Icon>,
    chevRight: <Icon size={14}><path d="m9 18 6-6-6-6" /></Icon>,
    file:      <Icon size={14}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></Icon>,
    bot:       <Icon size={12}><rect x="4" y="6" width="16" height="14" rx="2" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" /><path d="M12 2v4M8 20v2M16 20v2" /></Icon>,
    x:         <Icon size={12}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
};


// ── Configuraciones ──────────────────────────────────────────────────────────
const STATUS_COLORS = {
    open:      { bg: '#dbeafe', fg: '#1e40af', dot: '#2563eb' },
    bot:       { bg: '#ede9fe', fg: '#5b21b6', dot: '#7c3aed' },
    escalated: { bg: '#fef3c7', fg: '#92400e', dot: '#d97706' },
    active:    { bg: '#d1fae5', fg: '#065f46', dot: '#059669' },
    closed:    { bg: '#f3f4f6', fg: '#6b7280', dot: '#9ca3af' },
};
const STATUS_LABEL = {
    open: 'Nueva', bot: 'Bot', escalated: 'Escalada', active: 'Activa', closed: 'Cerrada',
};

// Rol de quien escribe (resuelto en el backend contra el padrón de intranet).
// 'titular' = personal de campo (en intranet); 'coordinadora' = nuestra clienta.
// null/desconocido → término neutral.
const roleLabel = (r) =>
    r === 'titular' ? 'Titular de ruta' : r === 'coordinadora' ? 'Coordinadora' : 'Cliente';

// Sub-estados derivados (se calculan en el backend en cada inboxList).
const SUB_STATUS_CFG = {
    unattended:     { label: 'Sin tocar',         bg: '#dbeafe', fg: '#1e40af', dot: '#2563eb' },
    waiting_agent:  { label: 'Esperando agente',  bg: '#fef3c7', fg: '#92400e', dot: '#d97706' },
    waiting_client: { label: 'Esperando cliente', bg: '#e0e7ff', fg: '#3730a3', dot: '#6366f1' },
    stale:          { label: 'Sin respuesta',     bg: '#fee2e2', fg: '#991b1b', dot: '#dc2626' },
    snoozed:        { label: 'Snoozed',           bg: '#f3e8ff', fg: '#6b21a8', dot: '#a855f7' },
    closed:         { label: 'Cerrada',           bg: '#f3f4f6', fg: '#6b7280', dot: '#9ca3af' },
};
const fmtSnooze = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' });
};


// ── Helpers ──────────────────────────────────────────────────────────────────
function relTime(iso) {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60)    return 'ahora';
    if (s < 3600)  return `${Math.floor(s/60)}m`;
    if (s < 86400) return `${Math.floor(s/3600)}h`;
    return new Date(iso).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' });
}
function fmtTime(iso) { return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' }); }
function isImage(mime) { return /^image\//.test(mime || ''); }
function isPdf(mime, name) { return mime === 'application/pdf' || /\.pdf$/i.test(name || ''); }
function isAudio(mime, name) { return /^audio\//.test(mime || '') || /\.(mp3|m4a|wav|ogg|webm)$/i.test(name || ''); }

function fmtWait(ms) {
    if (!ms || ms < 0) return '';
    const totalMin = Math.floor(ms / 60000);
    if (totalMin < 1)   return '< 1 min';
    if (totalMin < 60)  return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin - h * 60;
    if (h < 24)         return m ? `${h}h ${m}m` : `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d ${h - d * 24}h`;
}

function calcWait(conv, lastClientMsgAt) {
    if (!conv) return null;
    const now = Date.now();
    if (['open', 'bot', 'escalated'].includes(conv.status)) {
        const since = conv.openedAt || conv.createdAt;
        if (!since) return null;
        const ms = now - new Date(since).getTime();
        return { ms, label: `esperando ${fmtWait(ms)}` };
    }
    if (conv.status === 'active') {
        if (lastClientMsgAt) {
            const ms = now - new Date(lastClientMsgAt).getTime();
            if (ms < 60000) return null;
            return { ms, label: `sin respuesta hace ${fmtWait(ms)}` };
        }
        return null;
    }
    return null;
}

function waitColor(ms, slaConfig) {
    if (!ms) return '#6b7280';
    const warn   = (slaConfig?.warningMinutes       ?? 5)  * 60 * 1000;
    const target = (slaConfig?.firstResponseMinutes ?? 15) * 60 * 1000;
    if (ms < warn)   return '#15803d';
    if (ms < target) return '#b45309';
    return '#b91c1c';
}


// ── Scoped CSS ───────────────────────────────────────────────────────────────
const SI_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.si-root {
    --si-accent: var(--brand-700, #0a4aa8);
    --si-accent-hover: var(--brand-800, #07357f);
    --si-accent-light: #dbeafe;
    --si-radius-lg: 14px;
    --si-radius-md: 10px;
    --si-radius-sm: 8px;
    --si-shadow-sm: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1400px; margin: 0 auto;
    padding: 16px 20px;
    /* 100dvh evita el salto en iOS Safari cuando la barra de URL aparece/desaparece */
    height: calc(100dvh - 64px);
    display: flex; flex-direction: column;
    position: relative;
}

/* Botón "← Lista" — visible solo en móvil. En desktop se oculta. */
.si-mobile-back {
    display: none;
    background: transparent;
    border: 1px solid var(--c-border);
    color: var(--c-text-2);
    border-radius: var(--si-radius-sm);
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    flex-shrink: 0;
}
.si-mobile-back:hover { background: var(--c-msg-bg); }
.si-root * { box-sizing: border-box; }

.si-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 14px; flex: 0 0 auto;
}
.si-title {
    font-size: 22px; font-weight: 700; color: var(--c-text-1);
    margin: 0; letter-spacing: -0.01em;
}
.si-subtitle { color: var(--c-text-3); font-size: 13px; margin: 2px 0 0; }

/* Tabs */
.si-tabs {
    display: inline-flex; gap: 4px; padding: 4px;
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: 999px;
    margin-bottom: 14px;
    flex: 0 0 auto;
}
.si-tab {
    background: transparent; border: none;
    padding: 7px 16px; border-radius: 999px;
    font-size: 13px; font-weight: 600;
    color: var(--c-text-3);
    cursor: pointer; font-family: inherit;
    display: inline-flex; align-items: center; gap: 6px;
    transition: all .15s;
}
.si-tab:hover { color: var(--c-text-1); background: var(--c-msg-bg); }
.si-tab.active { background: var(--si-accent); color: #fff; }
.si-tab.active:hover { background: var(--si-accent-hover); color: #fff; }
.si-tab-badge {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 18px; height: 18px; padding: 0 5px;
    border-radius: 999px;
    background: rgba(255,255,255,.25);
    color: inherit;
    font-size: 10px; font-weight: 700;
}
.si-tab:not(.active) .si-tab-badge {
    background: #dc2626; color: #fff;
}

/* Grid 3 cols */
.si-grid {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr) 300px;
    gap: 12px;
    flex: 1; min-height: 0;
}

/* Panels (card-like) */
.si-panel {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: var(--si-radius-lg);
    box-shadow: var(--si-shadow-sm);
    display: flex; flex-direction: column; min-height: 0;
}
.si-list-panel { overflow-y: auto; }
.si-chat-panel { overflow: hidden; }
.si-info-panel { padding: 18px 20px; overflow-y: auto; }

/* Conversation list */
.si-conv {
    padding: 12px 14px;
    border-bottom: 1px solid var(--c-card-header-border);
    cursor: pointer;
    transition: background .15s;
    position: relative;
}
.si-conv:hover { background: var(--c-msg-bg); }
.si-conv.active { background: var(--si-accent-light); }
.si-conv.active::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px; background: var(--si-accent);
}
.si-conv-top {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 4px; gap: 6px;
}
.si-conv-name {
    font-size: 13.5px; font-weight: 600; color: var(--c-text-1);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.si-conv-num {
    color: var(--c-text-4); margin-right: 4px;
    font-variant-numeric: tabular-nums;
}
.si-conv-rel {
    font-size: 11px; color: var(--c-text-4); flex-shrink: 0;
    font-variant-numeric: tabular-nums;
}
.si-conv-meta {
    display: flex; gap: 5px; align-items: center;
    margin-bottom: 4px; flex-wrap: wrap;
}
.si-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border-radius: 999px;
    font-size: 10px; font-weight: 600; line-height: 1.4;
}
.si-pill-dot { width: 5px; height: 5px; border-radius: 50%; }
.si-pill-folio {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--c-msg-bg); color: var(--c-text-2);
    border: 1px solid var(--c-border);
    padding: 1px 7px; border-radius: 5px;
    font-size: 10px; letter-spacing: .3px;
}
.si-conv-tag { font-size: 10px; color: var(--c-text-4); }
.si-conv-unread {
    background: #dc2626; color: #fff;
    min-width: 18px; height: 18px; padding: 0 5px;
    border-radius: 999px;
    font-size: 10px; font-weight: 700;
    display: inline-flex; align-items: center; justify-content: center;
    margin-left: auto;
}
.si-conv-preview {
    font-size: 12px; color: var(--c-text-3);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.si-conv-wait {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10.5px; font-weight: 600; margin-top: 4px;
}
.si-conv-agent {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10.5px; color: #0f766e; margin-top: 3px;
}

.si-empty {
    color: var(--c-text-4); padding: 32px 20px; font-size: 13px;
    text-align: center;
}

/* Chat panel */
.si-chat-header {
    padding: 12px 18px;
    border-bottom: 1px solid var(--c-card-header-border);
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
    flex-shrink: 0;
}
.si-chat-name {
    font-size: 14.5px; font-weight: 700; color: var(--c-text-1);
}
.si-chat-meta {
    font-size: 11px; color: var(--c-text-4);
    display: flex; align-items: center; gap: 6px; margin-top: 2px;
    flex-wrap: wrap;
}

.si-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; border: 1px solid var(--c-border);
    color: var(--c-text-2);
    padding: 6px 12px; border-radius: var(--si-radius-sm);
    font-size: 12.5px; font-weight: 600;
    cursor: pointer; transition: all .15s;
    font-family: inherit;
}
.si-btn:hover:not(:disabled) { background: var(--c-msg-bg); color: var(--c-text-1); }
.si-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.si-btn-danger { color: #dc2626; border-color: #fecaca; }
.si-btn-danger:hover:not(:disabled) { background: var(--c-priority-urgent-bg); border-color: #dc2626; }
.si-btn-success { color: #15803d; border-color: #a7f3d0; }
.si-btn-success:hover:not(:disabled) { background: #d1fae5; border-color: #15803d; }
.si-btn-primary {
    background: var(--si-accent); color: #fff; border-color: var(--si-accent);
}
.si-btn-primary:hover:not(:disabled) {
    background: var(--si-accent-hover); border-color: var(--si-accent-hover);
}
.si-btn-icon {
    width: 38px; padding: 6px 0; justify-content: center;
}

/* Messages thread */
.si-thread {
    flex: 1; overflow-y: auto;
    padding: 18px 20px;
    background: var(--c-bg);
    min-height: 0;
}

/* AI summary banner */
.si-summary {
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: var(--si-radius-md);
    padding: 12px 14px;
    margin-bottom: 14px;
    color: #78350f;
}
.si-summary-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 6px; gap: 8px;
}
.si-summary-title {
    font-size: 11px; text-transform: uppercase; letter-spacing: .5px;
    font-weight: 700;
    display: inline-flex; align-items: center; gap: 6px;
}
.si-summary-time { font-size: 10px; opacity: .7; }
.si-summary-text {
    font-size: 13px; line-height: 1.5; white-space: pre-wrap;
}
.si-summary-reason { font-size: 11px; margin-top: 8px; opacity: .75; }
.si-summary-priv { font-size: 10px; margin-top: 6px; opacity: .65; font-style: italic; }

.si-history-toggle {
    width: 100%;
    padding: 9px 14px;
    background: var(--c-card);
    border: 1px dashed var(--c-border);
    border-radius: var(--si-radius-md);
    cursor: pointer;
    font-size: 12px; font-family: inherit;
    color: var(--c-text-3); text-align: left;
    display: inline-flex; align-items: center; gap: 8px;
    transition: background .15s;
}
.si-history-toggle:hover { background: var(--c-msg-bg); }
.si-history-body {
    margin-top: 8px; padding-top: 8px;
    border-top: 1px dashed var(--c-border);
    opacity: .85;
}

/* Message bubble */
.si-msg {
    display: flex; margin-bottom: 10px;
}
.si-msg.mine { justify-content: flex-end; }
.si-msg-bubble {
    max-width: 72%;
    padding: 9px 13px;
    border-radius: 14px;
    background: var(--c-msg-bg);
    color: var(--c-text-1);
    box-shadow: 0 1px 2px rgba(0,0,0,.04);
    border-top-left-radius: 4px;
}
.si-msg.mine .si-msg-bubble {
    background: var(--si-accent); color: #fff;
    border-top-left-radius: 14px;
    border-top-right-radius: 4px;
}
.si-msg.bot .si-msg-bubble {
    background: #7c3aed; color: #fff;
    border-top-left-radius: 14px;
    border-top-right-radius: 4px;
}
.si-msg-author {
    font-size: 10px; opacity: .85; margin-bottom: 2px; font-weight: 700;
    display: inline-flex; align-items: center; gap: 4px;
    text-transform: uppercase; letter-spacing: .4px;
}
.si-msg-text {
    font-size: 14px; line-height: 1.5;
    white-space: pre-wrap; word-break: break-word;
}
.si-msg-time {
    font-size: 10px; margin-top: 4px; text-align: right; opacity: .7;
    font-variant-numeric: tabular-nums;
}

/* Composer */
.si-composer {
    padding: 12px 14px;
    border-top: 1px solid var(--c-card-header-border);
    flex-shrink: 0;
    background: var(--c-card);
}
.si-files-row {
    display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;
}
.si-file-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--c-msg-bg);
    border: 1px solid var(--c-border);
    border-radius: 999px;
    padding: 4px 10px 4px 8px;
    font-size: 12px; color: var(--c-text-2);
}
.si-file-chip .remove {
    background: none; border: none; cursor: pointer; padding: 0;
    color: var(--c-text-4); display: inline-flex; align-items: center;
    transition: color .15s;
}
.si-file-chip .remove:hover { color: #dc2626; }

.si-composer-row {
    display: flex; gap: 6px; align-items: flex-end;
}
.si-attach-btn {
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    width: 40px; height: 40px;
    border-radius: var(--si-radius-md);
    background: var(--c-msg-bg);
    color: var(--c-text-3);
    border: 1px solid var(--c-border);
    transition: all .15s;
    flex-shrink: 0;
}
.si-attach-btn:hover { background: var(--si-accent-light); color: var(--si-accent); border-color: var(--si-accent); }
.si-textarea {
    flex: 1; resize: none;
    padding: 9px 12px;
    font-size: 14px; font-family: inherit; line-height: 1.5;
    border: 1px solid var(--c-border);
    border-radius: var(--si-radius-md);
    background: var(--c-input-bg);
    color: var(--c-text-1);
    outline: none;
    transition: border-color .15s, box-shadow .15s;
    min-height: 40px;
    max-height: 140px;
}
.si-textarea:focus {
    border-color: var(--si-accent);
    box-shadow: 0 0 0 3px rgba(10,74,168,.12);
}
.si-send-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    height: 40px;
    background: var(--si-accent); color: #fff;
    border: 1px solid var(--si-accent);
    border-radius: var(--si-radius-md);
    padding: 0 16px;
    font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
    flex-shrink: 0;
}
.si-send-btn:hover:not(:disabled) {
    background: var(--si-accent-hover); border-color: var(--si-accent-hover);
}
.si-send-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Customer info panel */
.si-info-section {
    margin-bottom: 18px;
}
.si-info-section h3 {
    font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .6px;
    color: var(--c-text-4); margin: 0 0 10px;
}
.si-info-name {
    font-size: 14px; font-weight: 700; color: var(--c-text-1); margin-bottom: 8px;
}
.si-info-row {
    margin-bottom: 5px;
    display: flex; gap: 8px; align-items: baseline;
    font-size: 12px;
}
.si-info-label {
    color: var(--c-text-4); font-weight: 600;
    text-transform: uppercase; font-size: 10px;
    letter-spacing: .3px;
    flex: 0 0 auto;
}
.si-info-value {
    color: var(--c-text-1);
    word-break: break-word;
}
.si-info-value.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; }

.si-account {
    background: var(--c-msg-bg);
    border-radius: var(--si-radius-sm);
    padding: 10px;
    display: flex; flex-direction: column; gap: 6px;
}
.si-account.compact { padding: 6px; }
.si-account-row {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 8px; font-size: 12px;
}
.si-account-key {
    color: var(--c-text-4); font-weight: 600; flex-shrink: 0;
}
.si-account-val {
    color: var(--c-text-1); text-align: right; word-break: break-word;
}
.si-account-nested {
    margin-left: 8px; margin-top: 4px;
    padding-left: 8px; border-left: 2px solid var(--c-border);
}

/* Drag overlay */
.si-drag {
    position: absolute; inset: 16px;
    background: rgba(10,74,168,.10);
    border: 3px dashed var(--si-accent);
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center; gap: 12px;
    color: var(--si-accent); font-size: 18px; font-weight: 700;
    z-index: 50; pointer-events: none;
}

/* Tablet: oculta info panel lateral, lista más compacta */
@media (max-width: 1100px) {
    .si-grid { grid-template-columns: 280px minmax(0, 1fr); }
    .si-info-panel { display: none; }
}

/* Móvil: patrón master-detail.
 *  - Sin conversación seleccionada → SOLO la lista, full screen.
 *  - Con conversación seleccionada (.mobile-detail en el root) → SOLO el chat,
 *    con un botón "← Lista" para volver.
 *  Antes era un stack lista 200px / chat resto — UX pobre (solo veías 2 convs
 *  a la vez y no había forma de "salir" del chat). */
@media (max-width: 768px) {
    .si-root {
        padding: 10px 10px;
        height: calc(100dvh - 56px);
    }
    .si-header { margin-bottom: 8px; }
    .si-title { font-size: 18px; }
    .si-subtitle { font-size: 11px; }
    .si-tabs { margin-bottom: 8px; flex-wrap: wrap; }
    .si-tab { padding: 6px 12px; font-size: 12px; }

    .si-grid {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
        gap: 0;
    }
    /* Default móvil: lista visible, chat oculto */
    .si-list-panel { max-height: none; }
    .si-chat-panel { display: none; }

    /* Conversación seleccionada: chat visible, lista oculta */
    .si-root.mobile-detail .si-list-panel { display: none; }
    .si-root.mobile-detail .si-chat-panel { display: flex; }

    /* Mostrar botón back y compactar header del chat */
    .si-mobile-back { display: inline-flex; align-items: center; }
    .si-chat-header { padding: 10px 12px; }
    .si-chat-name { font-size: 14px; }
}
`;


// ── Helpers de account data ──────────────────────────────────────────────────
function humanizeKey(key) {
    return String(key).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
}
function fmtCurrency(n) {
    try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n); } catch { return `$${n}`; }
}
function fmtDateStr(s) {
    try { const d = new Date(s); if (isNaN(d.getTime())) return s; return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; }
}
function isMonetaryKey(k) { return /(saldo|monto|importe|deuda|capital|interes|abono|pago|amount|balance|total)/i.test(k); }
function isDateKey(k) { return /(fecha|date|at|on|when|vence|venc|ultim|prox|inicio|fin)/i.test(k); }
function isDateValue(v) { return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v); }

function renderValue(key, val) {
    if (val === null || val === undefined || val === '') return <span style={{ color: 'var(--c-text-4)' }}>—</span>;
    if (typeof val === 'boolean') return val ? <span style={{ color: '#15803d', fontWeight: 600 }}>✓ Sí</span> : <span style={{ color: '#b91c1c', fontWeight: 600 }}>✗ No</span>;
    if (typeof val === 'number' && isMonetaryKey(key)) return <span style={{ fontWeight: 700, color: '#0f766e' }}>{fmtCurrency(val)}</span>;
    if (typeof val === 'number') return <span>{val.toLocaleString('es-MX')}</span>;
    if (isDateKey(key) || isDateValue(val)) return <span>{fmtDateStr(val)}</span>;
    if (Array.isArray(val)) return val.length ? <span>{val.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ')}</span> : <span style={{ color: 'var(--c-text-4)' }}>—</span>;
    if (typeof val === 'object') return <div className="si-account-nested"><AccountDataPanel data={val} compact /></div>;
    return <span>{String(val)}</span>;
}

function AccountDataPanel({ data, compact = false }) {
    const entries = Object.entries(data || {});
    if (!entries.length) return null;
    return (
        <div className={`si-account${compact ? ' compact' : ''}`}>
            {entries.map(([key, val]) => (
                <div key={key} className="si-account-row">
                    <span className="si-account-key">{humanizeKey(key)}</span>
                    <span className="si-account-val">{renderValue(key, val)}</span>
                </div>
            ))}
        </div>
    );
}

function Field({ label, value, mono }) {
    return (
        <div className="si-info-row">
            <span className="si-info-label">{label}</span>
            <span className={`si-info-value${mono ? ' mono' : ''}`}>{value || '—'}</span>
        </div>
    );
}


// ── Componente principal ─────────────────────────────────────────────────────
export default function SupportInbox() {
    const { user } = useContext(AuthContext);
    const [queue, setQueue]   = useState('unattended');
    const [list, setList]     = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingChat, setLoadingChat] = useState(false);

    const [text, setText]   = useState('');
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const dragCounter = useRef(0);

    // Toggle global del bot (visible para todo el staff; solo admin/leader puede cambiarlo).
    const [botEnabled, setBotEnabled] = useState(true);
    const [botToggling, setBotToggling] = useState(false);
    const canToggleBot = ['admin', 'leader'].includes(user?.role);
    useEffect(() => {
        getBotStatus().then(r => setBotEnabled(!!r.enabled)).catch(() => {});
    }, []);
    const onToggleBot = async () => {
        if (!canToggleBot || botToggling) return;
        const next = !botEnabled;
        setBotToggling(true);
        try {
            const r = await setBotStatus(next);
            setBotEnabled(!!r.enabled);
            toast.success(r.enabled ? 'Bot activado' : 'Bot desactivado — nuevos chats van directo a humano');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cambiar el estado del bot');
        } finally {
            setBotToggling(false);
        }
    };

    const scrollRef = useRef(null);
    const activeIdRef = useRef(null);
    useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

    const queueRef = useRef(queue);
    useEffect(() => { queueRef.current = queue; }, [queue]);

    const [, setNowTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setNowTick(t => t + 1), 30 * 1000);
        return () => clearInterval(id);
    }, []);

    const [slaConfig, setSlaConfig] = useState(null);
    useEffect(() => {
        api.get('/support/sla').then(r => setSlaConfig(r.data)).catch(() => {});
    }, []);

    // ── Sprint UI/UX: métricas globales para badges en tabs ──────────────────
    const [metrics, setMetrics] = useState({ unattended: 0, stale30: 0, snoozed: 0, myActive: 0, closedToday: 0 });
    const loadMetrics = () => inboxMetrics().then(setMetrics).catch(() => {});
    useEffect(() => {
        loadMetrics();
        const id = setInterval(loadMetrics, 60 * 1000); // refrescar cada minuto
        return () => clearInterval(id);
    }, []);

    // ── Sprint UI/UX: contexto del cliente (sidebar) ──────────────────────────
    const [clientCtx, setClientCtx]   = useState(null);
    const [ctxLoading, setCtxLoading] = useState(false);
    useEffect(() => {
        if (!activeId) { setClientCtx(null); return; }
        setCtxLoading(true);
        clientContext(activeId)
            .then(setClientCtx)
            .catch(() => setClientCtx(null))
            .finally(() => setCtxLoading(false));
    }, [activeId]);

    // ── Sprint UI/UX: lista de agentes para transferir (cache 1 vez) ─────────
    const [agentsList, setAgentsList] = useState([]);
    useEffect(() => {
        listSupportAgents()
            .then(arr => setAgentsList(
                (arr || []).filter(u => ['agent', 'leader', 'authorizer', 'admin'].includes(u.role))
            ))
            .catch(() => {});
    }, []);

    // Modal/dropdown states
    const [transferOpen, setTransferOpen] = useState(false);
    const [snoozeOpen,   setSnoozeOpen]   = useState(false);
    const [actingAux,    setActingAux]    = useState(false);

    const audioCtxRef = useRef(null);
    const baseTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');
    const unreadStaffRef = useRef(0);
    const playPing = () => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
            const ctx = audioCtxRef.current;
            if (ctx.state !== 'running') return;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = 660;
            gain.gain.setValueAtTime(0.001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
            osc.connect(gain).connect(ctx.destination);
            osc.start(); osc.stop(ctx.currentTime + 0.2);
        } catch { /* noop */ }
    };
    const flashTitle = () => {
        if (!document.hidden) return;
        unreadStaffRef.current += 1;
        document.title = `(${unreadStaffRef.current}) ${baseTitleRef.current}`;
    };
    useEffect(() => {
        const onVis = () => {
            if (!document.hidden) {
                unreadStaffRef.current = 0;
                document.title = baseTitleRef.current;
            }
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    const reload = async () => {
        try {
            const data = await inboxList({ queue });
            setList(data || []);
        } catch (e) {
            console.error(e);
            toast.error('No se pudo cargar inbox');
        } finally { setLoading(false); }
    };
    useEffect(() => { setLoading(true); reload(); /* eslint-disable-next-line */ }, [queue]);

    useEffect(() => {
        if (!activeId) { setActive(null); return; }
        setShowHistory(false);
        setLoadingChat(true);
        inboxDetail(activeId)
            .then(setActive)
            .catch(() => toast.error('No se pudo cargar la conversación'))
            .finally(() => {
                setLoadingChat(false);
                requestAnimationFrame(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                });
            });
    }, [activeId]);

    const matchesQueue = (conv, q) => {
        if (!conv) return false;
        const now = Date.now();
        const isSnoozed = conv.snoozeUntil && new Date(conv.snoozeUntil).getTime() > now;
        if (q === 'unattended') return ['open', 'bot', 'escalated'].includes(conv.status) && !isSnoozed;
        if (q === 'attending')  return conv.status === 'active' && !isSnoozed;
        if (q === 'mine') {
            return conv.status !== 'closed'
                && String(conv.assignedTo?._id || conv.assignedTo) === String(user?.id);
        }
        if (q === 'snoozed') return !!isSnoozed && conv.status !== 'closed';
        if (q === 'stale') {
            return conv.status !== 'closed' && !isSnoozed
                && conv.lastMessage?.from === 'client'
                && conv.lastClientMsgAt
                && (now - new Date(conv.lastClientMsgAt).getTime() > 30 * 60 * 1000);
        }
        if (q === 'closed') return conv.status === 'closed';
        return true;
    };

    // Self-healing: si llega un evento (mensaje/update) para una conv que no
    // está en la lista (porque el supportConvCreated se perdió o el socket
    // se reconectó), fetcheamos su detalle y la insertamos si pertenece al
    // queue actual. Evita el "tengo que dar F5 para que aparezca".
    const ensureConvInList = (convId, opts = {}) => {
        const { fromMessage } = opts;
        inboxDetail(convId)
            .then(conv => {
                if (!matchesQueue(conv, queueRef.current)) return;
                setList(prev => prev.some(c => String(c._id) === String(conv._id)) ? prev : [conv, ...prev]);
                if (fromMessage === 'client') { playPing(); flashTitle(); }
            })
            .catch(() => { /* silencioso: el siguiente reload lo trae */ });
    };

    useEffect(() => {
        const onCreated = (conv) => {
            if (!matchesQueue(conv, queueRef.current)) return;
            setList(prev => prev.some(c => String(c._id) === String(conv._id)) ? prev : [conv, ...prev]);
            toast.info(`Nueva conversación: ${conv.snapshot?.name || 'cliente'}`, { autoClose: 3000 });
            playPing();
            flashTitle();
        };
        const onMessage = ({ conversationId, message }) => {
            let convExistsInList = false;
            setList(prev => {
                const idx = prev.findIndex(c => String(c._id) === String(conversationId));
                if (idx < 0) return prev;
                convExistsInList = true;
                const updated = [...prev];
                updated[idx] = {
                    ...prev[idx],
                    lastMessage: { from: message.from, text: (message.text || (message.attachments?.length ? '📎 Adjunto' : '')).slice(0, 120), at: message.createdAt },
                    lastMessageAt: message.createdAt,
                    unreadByStaff: (String(activeIdRef.current) === String(conversationId)) ? 0 : (prev[idx].unreadByStaff || 0) + (message.from === 'client' ? 1 : 0),
                };
                return updated;
            });
            // Si la conv NO estaba en la lista, fetch e insertar (auto-cura
            // cuando se perdió el supportConvCreated).
            if (!convExistsInList) ensureConvInList(conversationId, { fromMessage: message.from });

            if (message.from === 'client' && String(activeIdRef.current) !== String(conversationId)) {
                playPing();
                flashTitle();
            }
            if (String(activeIdRef.current) === String(conversationId)) {
                setActive(prev => {
                    if (!prev) return prev;
                    const exists = (prev.messages || []).some(m => String(m._id) === String(message._id));
                    return exists ? prev : { ...prev, messages: [...(prev.messages || []), message] };
                });
                requestAnimationFrame(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                });
            }
        };
        const onConvUpdated = (patch) => {
            let convExistsInList = false;
            setList(prev => {
                const idx = prev.findIndex(c => String(c._id) === String(patch._id));
                if (idx < 0) return prev;
                convExistsInList = true;
                return prev
                    .map(c => String(c._id) === String(patch._id) ? { ...c, ...patch } : c)
                    .filter(c => matchesQueue(c, queueRef.current));
            });
            // Si la conv no estaba en la lista pero su nuevo status la pondría
            // dentro del queue actual, fetch e insertar (auto-cura: ej. conv
            // que pasó de 'closed' a 'escalated' por reopen).
            if (!convExistsInList && patch.status && matchesQueue({ status: patch.status }, queueRef.current)) {
                ensureConvInList(patch._id);
            }
            if (String(activeIdRef.current) === String(patch._id)) {
                setActive(prev => prev ? { ...prev, ...patch } : prev);
            }
        };
        const onBotStatus = (p) => setBotEnabled(!!p?.enabled);
        // Cuando el socket reconecta (cambió de red, dormida del laptop, etc.)
        // re-fetcheamos la lista — los eventos durante la desconexión se perdieron.
        // Inline con queueRef para evitar capturar el queue inicial del primer render.
        const onReconnect = () => {
            inboxList({ queue: queueRef.current })
                .then(d => setList(d || []))
                .catch(() => {});
        };
        socket.on('connect',            onReconnect);
        socket.on('supportConvCreated', onCreated);
        socket.on('supportMessage',     onMessage);
        socket.on('supportConvUpdated', onConvUpdated);
        socket.on('supportBotStatus',   onBotStatus);
        return () => {
            socket.off('connect',            onReconnect);
            socket.off('supportConvCreated', onCreated);
            socket.off('supportMessage',     onMessage);
            socket.off('supportConvUpdated', onConvUpdated);
            socket.off('supportBotStatus',   onBotStatus);
        };
    }, []);

    // Composer: adjuntos
    const addFiles = (incoming) => {
        const picked = Array.from(incoming || []);
        if (!picked.length) return;
        const MAX_SIZE = 25 * 1024 * 1024;
        const oversize = picked.find(f => f.size > MAX_SIZE);
        if (oversize) { toast.warn(`"${oversize.name}" supera 25 MB`); return; }
        if (files.length + picked.length > 5) { toast.warn('Máximo 5 archivos'); return; }
        setFiles(prev => [...prev, ...picked]);
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));

    const onDragEnter = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault(); dragCounter.current += 1; setDragOver(true);
    };
    const onDragOver = (e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } };
    const onDragLeave = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        dragCounter.current = Math.max(0, dragCounter.current - 1);
        if (dragCounter.current === 0) setDragOver(false);
    };
    const onDrop = (e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault(); dragCounter.current = 0; setDragOver(false);
        addFiles(e.dataTransfer.files);
    };
    const onPaste = (e) => {
        const fromClip = Array.from(e.clipboardData?.items || [])
            .filter(it => it.kind === 'file').map(it => it.getAsFile()).filter(Boolean);
        if (fromClip.length) { e.preventDefault(); addFiles(fromClip); }
    };

    const handleSend = async (e) => {
        e?.preventDefault?.();
        if ((!text.trim() && !files.length) || !activeId || sending) return;
        setSending(true);
        try {
            const msg = await sendAgent(activeId, { text: text.trim(), files });
            setText(''); setFiles([]);
            setActive(prev => {
                if (!prev) return prev;
                const exists = (prev.messages || []).some(m => String(m._id) === String(msg._id));
                return exists ? prev : { ...prev, messages: [...(prev.messages || []), msg] };
            });
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            });
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo enviar');
        } finally { setSending(false); }
    };

    const handleClose = async () => {
        if (!activeId) return;
        if (!window.confirm('¿Cerrar esta conversación? El ticket asociado también se marca cerrado.')) return;
        try {
            await closeConv(activeId);
            toast.success('Conversación cerrada');
        } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    };

    const handleReopen = async () => {
        if (!activeId) return;
        if (!window.confirm('¿Reabrir esta conversación? Vuelve al inbox y el ticket se reactiva.')) return;
        try {
            const res = await reopenConv(activeId);
            toast.success(`Conversación reabierta (${res.status})`);
        } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    };

    const handleEscalateTech = async () => {
        if (!activeId || actingAux) return;
        const note = window.prompt(
            'Vas a escalar este caso a Base de Datos Présico (el área técnica).\n\n' +
            'Describe brevemente el problema o por qué rebasa tu área. ' +
            'El equipo lo verá como comentario interno junto con el resumen de la IA y la conversación completa:'
        );
        if (note === null) return;             // cancelado
        const clean = note.trim();
        if (!clean) { toast.warn('La descripción es obligatoria'); return; }
        if (!window.confirm(
            '¿Escalar este caso a Base de Datos Présico?\n\n' +
            '• El chat con el cliente se cierra con un mensaje automático\n' +
            '• Tu nota + resumen de la IA + conversación completa van al ticket\n' +
            '• El equipo de Base de Datos recibe notificación en su navegador'
        )) return;
        setActingAux(true);
        try {
            const r = await escalateToTech(activeId, clean);
            const msg = r.action === 'intervention_requested'
                ? `Aviso urgente enviado a ${r.movedTo} (#${r.folio})`
                : `Caso escalado a ${r.movedTo} (#${r.folio})`;
            toast.success(msg);
            loadMetrics();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo escalar'); }
        finally { setActingAux(false); }
    };

    // ── Sprint UI/UX: Tomar / Transferir / Snooze ────────────────────────────
    const handleTake = async () => {
        if (!activeId || actingAux) return;
        setActingAux(true);
        try {
            await takeConv(activeId);
            toast.success('Conversación tomada');
            loadMetrics();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo tomar'); }
        finally { setActingAux(false); }
    };
    const handleTransfer = async (toUserId, toUserName) => {
        if (!activeId || actingAux || !toUserId) return;
        setActingAux(true);
        try {
            await transferConv(activeId, toUserId);
            toast.success(`Transferida a ${toUserName}`);
            setTransferOpen(false);
            loadMetrics();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo transferir'); }
        finally { setActingAux(false); }
    };
    const handleSnoozeMinutes = async (mins) => {
        if (!activeId || actingAux) return;
        setActingAux(true);
        try {
            await snoozeConv(activeId, { minutes: mins });
            toast.success(`Snooze ${mins} min`);
            setSnoozeOpen(false);
            loadMetrics();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo snooze'); }
        finally { setActingAux(false); }
    };
    const handleWakeUp = async () => {
        if (!activeId || actingAux) return;
        setActingAux(true);
        try {
            await snoozeConv(activeId, {}); // sin args = despertar
            toast.success('Conversación reactivada');
            loadMetrics();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo reactivar'); }
        finally { setActingAux(false); }
    };

    // Conteo para badges en tabs — usa métricas del backend (cualquier queue)
    const unattendedCount = queue === 'unattended' ? list.length : null;

    return (
        <>
            <style>{SI_CSS}</style>
            <div className={`si-root${activeId ? ' mobile-detail' : ''}`}
                 onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>

                {dragOver && (
                    <div className="si-drag">
                        {I.paperclip}
                        Suelta los archivos para adjuntarlos
                    </div>
                )}

                <div className="si-header">
                    <div>
                        <h1 className="si-title">Atención a Clientes</h1>
                        <p className="si-subtitle">Conversaciones desde la app Presico</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                            background: botEnabled ? '#d1fae5' : '#fee2e2',
                            color:      botEnabled ? '#065f46' : '#991b1b',
                            border: '1px solid ' + (botEnabled ? '#a7f3d0' : '#fecaca'),
                        }}>
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%',
                                background: botEnabled ? '#059669' : '#dc2626',
                            }} />
                            Bot {botEnabled ? 'activo' : 'pausado'}
                        </span>
                        {canToggleBot && (
                            <button
                                type="button"
                                onClick={onToggleBot}
                                disabled={botToggling}
                                title={botEnabled
                                    ? 'Apagar bot: los nuevos mensajes escalan directo a un agente humano'
                                    : 'Encender bot: vuelve a responder automáticamente las nuevas consultas'}
                                style={{
                                    padding: '6px 14px', borderRadius: 8,
                                    fontSize: 13, fontWeight: 600, cursor: botToggling ? 'wait' : 'pointer',
                                    border: '1px solid ' + (botEnabled ? '#dc2626' : '#059669'),
                                    background: botEnabled ? '#dc2626' : '#059669',
                                    color: '#fff', opacity: botToggling ? 0.6 : 1,
                                }}
                            >
                                {botToggling ? '…' : (botEnabled ? 'Apagar bot' : 'Encender bot')}
                            </button>
                        )}
                    </div>
                </div>

                <div className="si-tabs">
                    <button type="button" className={`si-tab${queue === 'unattended' ? ' active' : ''}`} onClick={() => setQueue('unattended')}>
                        Sin atender
                        {metrics.unattended > 0 && <span className="si-tab-badge">{metrics.unattended}</span>}
                    </button>
                    <button type="button" className={`si-tab${queue === 'attending' ? ' active' : ''}`} onClick={() => setQueue('attending')}>
                        En atención
                    </button>
                    <button type="button" className={`si-tab${queue === 'mine' ? ' active' : ''}`} onClick={() => setQueue('mine')}>
                        Mías
                        {metrics.myActive > 0 && <span className="si-tab-badge">{metrics.myActive}</span>}
                    </button>
                    <button type="button"
                        className={`si-tab${queue === 'stale' ? ' active' : ''}`}
                        onClick={() => setQueue('stale')}
                        style={metrics.stale30 > 0 ? { color: '#b91c1c' } : undefined}>
                        Vencidas
                        {metrics.stale30 > 0 && <span className="si-tab-badge" style={{ background: '#dc2626', color: '#fff' }}>{metrics.stale30}</span>}
                    </button>
                    <button type="button" className={`si-tab${queue === 'snoozed' ? ' active' : ''}`} onClick={() => setQueue('snoozed')}>
                        Snoozed
                        {metrics.snoozed > 0 && <span className="si-tab-badge">{metrics.snoozed}</span>}
                    </button>
                    <button type="button" className={`si-tab${queue === 'closed' ? ' active' : ''}`} onClick={() => setQueue('closed')}>
                        Cerradas
                    </button>
                </div>

                <div className="si-grid">
                    {/* ── Lista ── */}
                    <div className="si-panel si-list-panel">
                        {loading ? (
                            <div className="si-empty"><Spinner size="sm" animation="border" /></div>
                        ) : list.length === 0 ? (
                            <p className="si-empty">
                                {queue === 'unattended' ? '✓ No hay conversaciones esperando' :
                                 queue === 'attending'  ? 'Sin conversaciones activas' :
                                                         'Sin conversaciones cerradas'}
                            </p>
                        ) : list.map((c, idx) => {
                            const sc = STATUS_COLORS[c.status] || STATUS_COLORS.open;
                            const lastClientMsgAt = (c.status === 'active' && c.lastMessage?.from === 'client')
                                ? c.lastMessageAt : null;
                            const wait = calcWait(c, lastClientMsgAt);
                            return (
                                <div key={c._id}
                                    onClick={() => setActiveId(c._id)}
                                    className={`si-conv${String(c._id) === String(activeId) ? ' active' : ''}`}
                                >
                                    <div className="si-conv-top">
                                        <span className="si-conv-name">
                                            {queue === 'unattended' && <span className="si-conv-num">#{idx + 1}</span>}
                                            {c.client?.name || c.snapshot?.name || 'Cliente'}
                                        </span>
                                        <span className="si-conv-rel">{relTime(c.lastMessageAt || c.createdAt)}</span>
                                    </div>
                                    <div className="si-conv-meta">
                                        <span className="si-pill" style={{ background: sc.bg, color: sc.fg }}>
                                            <span className="si-pill-dot" style={{ background: sc.dot }} />
                                            {STATUS_LABEL[c.status] || c.status}
                                        </span>
                                        {c.subStatus && SUB_STATUS_CFG[c.subStatus] && c.subStatus !== c.status && (
                                            <span className="si-pill" style={{
                                                background: SUB_STATUS_CFG[c.subStatus].bg,
                                                color:      SUB_STATUS_CFG[c.subStatus].fg,
                                            }}>
                                                <span className="si-pill-dot" style={{ background: SUB_STATUS_CFG[c.subStatus].dot }} />
                                                {SUB_STATUS_CFG[c.subStatus].label}
                                            </span>
                                        )}
                                        {c.ticket?.folio && (
                                            <span className="si-pill-folio">#{c.ticket.folio}</span>
                                        )}
                                        {c.snapshot?.marca && <span className="si-conv-tag">{c.snapshot.marca}</span>}
                                        {c.snapshot?.pais && <span className="si-conv-tag">· {c.snapshot.pais}</span>}
                                        {c.unreadByStaff > 0 && <span className="si-conv-unread">{c.unreadByStaff}</span>}
                                    </div>
                                    {c.snoozeUntil && new Date(c.snoozeUntil) > new Date() && (
                                        <div className="si-conv-wait" style={{ color: '#6b21a8' }}>
                                            {I.clock} Snooze hasta {fmtSnooze(c.snoozeUntil)}
                                        </div>
                                    )}
                                    <div className="si-conv-preview">
                                        {c.lastMessage?.text || '—'}
                                    </div>
                                    {wait && (
                                        <div className="si-conv-wait" style={{ color: waitColor(wait.ms, slaConfig) }}>
                                            {I.clock} {wait.label}
                                        </div>
                                    )}
                                    {c.assignedTo?.name && (
                                        <div className="si-conv-agent">
                                            {I.user} {c.assignedTo.name}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Chat ── */}
                    <div className="si-panel si-chat-panel">
                        {!activeId ? (
                            <div className="si-empty" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Selecciona una conversación
                            </div>
                        ) : loadingChat ? (
                            <div className="si-empty" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Spinner animation="border" />
                            </div>
                        ) : (
                            <>
                                <div className="si-chat-header">
                                    <button type="button"
                                        className="si-mobile-back"
                                        onClick={() => setActiveId(null)}
                                        aria-label="Volver a la lista">
                                        ← Lista
                                    </button>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="si-chat-name">{active?.client?.name || 'Cliente'}</div>
                                        <div className="si-chat-meta">
                                            {active?.ticket?.folio && <span className="si-pill-folio">#{active.ticket.folio}</span>}
                                            <span>{STATUS_LABEL[active?.status] || active?.status}</span>
                                            {active?.assignedTo?.name && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0f766e' }}>
                                                    · {I.user} {active.assignedTo.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', position: 'relative' }}>
                                        {active?.status !== 'closed' && (() => {
                                            const isAssignedToMe = String(active?.assignedTo?._id || active?.assignedTo) === String(user?.id);
                                            const isSnoozed = active?.snoozeUntil && new Date(active.snoozeUntil) > new Date();
                                            return (
                                                <>
                                                    {!isAssignedToMe && (
                                                        <button type="button" onClick={handleTake} disabled={actingAux}
                                                            title="Tomar la conversación (se te asigna)"
                                                            style={{
                                                                padding: '6px 12px', borderRadius: 8, border: '1px solid #059669',
                                                                background: '#d1fae5', color: '#065f46',
                                                                fontSize: 13, fontWeight: 600, cursor: actingAux ? 'wait' : 'pointer',
                                                            }}>
                                                            Tomar
                                                        </button>
                                                    )}
                                                    <button type="button" onClick={() => { setTransferOpen(v => !v); setSnoozeOpen(false); }}
                                                        title="Transferir a otro agente"
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: '1px solid #2563eb',
                                                            background: '#dbeafe', color: '#1e40af',
                                                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                                        }}>
                                                        Transferir
                                                    </button>
                                                    {isSnoozed ? (
                                                        <button type="button" onClick={handleWakeUp} disabled={actingAux}
                                                            title={`Snoozed hasta ${fmtSnooze(active.snoozeUntil)} — despertar ahora`}
                                                            style={{
                                                                padding: '6px 12px', borderRadius: 8, border: '1px solid #a855f7',
                                                                background: '#f3e8ff', color: '#6b21a8',
                                                                fontSize: 13, fontWeight: 600, cursor: actingAux ? 'wait' : 'pointer',
                                                            }}>
                                                            Despertar
                                                        </button>
                                                    ) : (
                                                        <button type="button" onClick={() => { setSnoozeOpen(v => !v); setTransferOpen(false); }}
                                                            title="Posponer la conversación"
                                                            style={{
                                                                padding: '6px 12px', borderRadius: 8, border: '1px solid #a855f7',
                                                                background: '#f3e8ff', color: '#6b21a8',
                                                                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                                            }}>
                                                            Snooze
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={handleEscalateTech}
                                                        disabled={actingAux}
                                                        title="Escalar este caso a Base de Datos Présico (cuando rebasa tu área)"
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: '1px solid #b45309',
                                                            background: '#d97706', color: '#fff',
                                                            fontSize: 13, fontWeight: 700, cursor: actingAux ? 'wait' : 'pointer',
                                                        }}>
                                                        ⬆ Escalar a Base de Datos
                                                    </button>
                                                </>
                                            );
                                        })()}
                                        {active?.status !== 'closed' ? (
                                            <button type="button" className="si-btn si-btn-danger" onClick={handleClose}>Cerrar</button>
                                        ) : (
                                            <button type="button" className="si-btn si-btn-success" onClick={handleReopen}>Reabrir</button>
                                        )}
                                        {transferOpen && (
                                            <div style={{
                                                position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
                                                background: 'var(--c-card)', border: '1px solid var(--c-border)',
                                                borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,.12)',
                                                width: 260, maxHeight: 320, overflow: 'auto', padding: 6,
                                            }}>
                                                <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: .5, fontWeight: 700 }}>
                                                    Transferir a
                                                </div>
                                                {agentsList.length === 0
                                                    ? <div style={{ padding: 10, fontSize: 13, color: 'var(--c-text-3)' }}>Sin agentes disponibles</div>
                                                    : agentsList.filter(a => String(a._id) !== String(user?.id)).map(a => (
                                                        <button key={a._id} type="button"
                                                            onClick={() => handleTransfer(a._id, a.name)}
                                                            disabled={actingAux}
                                                            style={{
                                                                display: 'block', width: '100%', textAlign: 'left',
                                                                padding: '8px 10px', border: 'none', background: 'transparent',
                                                                fontSize: 13, cursor: actingAux ? 'wait' : 'pointer',
                                                                borderRadius: 6, color: 'var(--c-text-1)',
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--c-msg-bg)'}
                                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                            {a.name}
                                                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--c-text-4)' }}>· {a.role}</span>
                                                        </button>
                                                    ))}
                                            </div>
                                        )}
                                        {snoozeOpen && (
                                            <div style={{
                                                position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
                                                background: 'var(--c-card)', border: '1px solid var(--c-border)',
                                                borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,.12)',
                                                width: 220, padding: 6,
                                            }}>
                                                <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: .5, fontWeight: 700 }}>
                                                    Posponer hasta
                                                </div>
                                                {[15, 30, 60, 120, 240, 480].map(m => (
                                                    <button key={m} type="button"
                                                        onClick={() => handleSnoozeMinutes(m)}
                                                        disabled={actingAux}
                                                        style={{
                                                            display: 'block', width: '100%', textAlign: 'left',
                                                            padding: '8px 10px', border: 'none', background: 'transparent',
                                                            fontSize: 13, cursor: actingAux ? 'wait' : 'pointer',
                                                            borderRadius: 6, color: 'var(--c-text-1)',
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--c-msg-bg)'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                        {m < 60 ? `${m} min` : `${m / 60}h`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div ref={scrollRef} className="si-thread">
                                    {(() => {
                                        const messages = active?.messages || [];
                                        const summary  = active?.aiSummary;
                                        const hasSummary = !!(summary?.text);
                                        const firstAttended = active?.firstAttendedAt ? new Date(active.firstAttendedAt).getTime() : null;
                                        const previous = []; const current = [];
                                        messages.forEach(m => {
                                            const t = new Date(m.createdAt).getTime();
                                            if (firstAttended != null) {
                                                if (t < firstAttended) previous.push(m); else current.push(m);
                                            } else if (hasSummary) {
                                                previous.push(m);
                                            } else {
                                                current.push(m);
                                            }
                                        });

                                        const renderMsg = (m) => {
                                            const isAgent = m.from === 'agent';
                                            const isBot   = m.from === 'bot';
                                            const cls = `si-msg${isAgent ? ' mine' : ''}${isBot ? ' mine bot' : ''}`;
                                            return (
                                                <div key={m._id} className={cls}>
                                                    <div className="si-msg-bubble">
                                                        {(isAgent || isBot) && (
                                                            <div className="si-msg-author">
                                                                {isBot ? <>{I.bot} Bot</> : (m.senderUser?.name || 'Agente')}
                                                            </div>
                                                        )}
                                                        {m.text && <div className="si-msg-text">{m.text}</div>}
                                                        {(m.attachments || []).map((a, i) => (
                                                            <SupportAttachment key={i} msgId={m._id} idx={i} att={a} mine={isAgent || isBot} />
                                                        ))}
                                                        <div className="si-msg-time">
                                                            {fmtTime(m.createdAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        };

                                        return (
                                            <>
                                                {hasSummary && (
                                                    <div className="si-summary">
                                                        <div className="si-summary-head">
                                                            <span className="si-summary-title">
                                                                {I.sparkles} Resumen del caso · interno
                                                            </span>
                                                            {summary.generatedAt && (
                                                                <span className="si-summary-time">
                                                                    {new Date(summary.generatedAt).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="si-summary-text">
                                                            {summary.text}
                                                        </div>
                                                        {summary.escalateReason && (
                                                            <div className="si-summary-reason">
                                                                Motivo escalate: <em>{summary.escalateReason}</em>
                                                            </div>
                                                        )}
                                                        <div className="si-summary-priv">
                                                            El cliente no ve este resumen.
                                                        </div>
                                                    </div>
                                                )}

                                                {previous.length > 0 && (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <button
                                                            type="button"
                                                            className="si-history-toggle"
                                                            onClick={() => setShowHistory(s => !s)}
                                                        >
                                                            {showHistory ? I.chevDown : I.chevRight}
                                                            <span>
                                                                {firstAttended != null ? 'Mensajes previos del bot' : 'Caso completo'}
                                                                {' '}({previous.length} {previous.length === 1 ? 'mensaje' : 'mensajes'})
                                                            </span>
                                                        </button>
                                                        {showHistory && (
                                                            <div className="si-history-body">
                                                                {previous.map(renderMsg)}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {current.map(renderMsg)}
                                            </>
                                        );
                                    })()}
                                </div>

                                {active?.status !== 'closed' && (
                                    <div className="si-composer">
                                        {files.length > 0 && (
                                            <div className="si-files-row">
                                                {files.map((f, i) => (
                                                    <div key={i} className="si-file-chip">
                                                        {I.paperclip}
                                                        {f.name}
                                                        <button type="button" className="remove" onClick={() => removeFile(i)} aria-label="Quitar">
                                                            {I.x}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="si-composer-row">
                                            <label className="si-attach-btn" title="Adjuntar archivo">
                                                {I.paperclip}
                                                <input type="file" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value=''; }} />
                                            </label>
                                            <textarea
                                                rows={1}
                                                className="si-textarea"
                                                placeholder="Escribe… (Enter envía, Shift+Enter salto de línea)"
                                                value={text}
                                                onChange={e => setText(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleSend();
                                                    }
                                                }}
                                                onPaste={onPaste}
                                            />
                                            <button
                                                type="button"
                                                className="si-send-btn"
                                                onClick={handleSend}
                                                disabled={sending || (!text.trim() && !files.length)}
                                            >
                                                {sending ? <Spinner size="sm" animation="border" /> : <>{I.send} Enviar</>}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* ── Coordinadora info ── */}
                    <div className="si-panel si-info-panel">
                        {!active ? (
                            <p style={{ color: 'var(--c-text-4)', fontSize: 13 }}>Sin selección.</p>
                        ) : (
                            <>
                                <div className="si-info-section">
                                    <h3>{roleLabel(active.client?.role)}</h3>
                                    <div className="si-info-name">{active.client?.name || '—'}</div>
                                    <Field label="Email"      value={active.client?.email} />
                                    <Field label="Teléfono"   value={active.client?.phone} />
                                    <Field label="Marca"      value={active.client?.marca} />
                                    <Field label="País"       value={active.client?.pais} />
                                    <Field label="Ruta"       value={active.client?.ruta} />
                                    <Field label="Localidad"  value={active.client?.localidad} />
                                    <Field label="ID ext."    value={active.client?.externalClientId} mono />
                                </div>

                                <div className="si-info-section">
                                    <h3>Ticket</h3>
                                    <Field label="Folio"   value={active.ticket?.folio ? `#${active.ticket.folio}` : '—'} />
                                    <Field label="Estado"  value={active.ticket?.status} />
                                    <Field label="Agente"  value={active.assignedTo?.name || '— sin asignar —'} />
                                    {active.snoozeUntil && new Date(active.snoozeUntil) > new Date() && (
                                        <Field label="Snooze hasta" value={fmtSnooze(active.snoozeUntil)} />
                                    )}
                                </div>

                                {/* Historial del cliente (Sprint UI/UX) */}
                                <div className="si-info-section">
                                    <h3>Historial del cliente</h3>
                                    {ctxLoading ? (
                                        <div style={{ fontSize: 12, color: 'var(--c-text-4)' }}>Cargando…</div>
                                    ) : clientCtx ? (
                                        <>
                                            <Field label="Total conversaciones" value={String(clientCtx.stats?.totalConversations || 1)} />
                                            <Field label="Tickets abiertos"     value={String(clientCtx.stats?.openTickets || 0)} />
                                            {(clientCtx.previousConversations || []).length > 0 && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ fontSize: 11, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: .5, fontWeight: 700, marginBottom: 4 }}>
                                                        Convs previas
                                                    </div>
                                                    {clientCtx.previousConversations.slice(0, 5).map(pc => (
                                                        <div key={pc._id} onClick={() => setActiveId(pc._id)}
                                                            style={{
                                                                padding: '6px 8px', borderRadius: 6, fontSize: 12,
                                                                cursor: 'pointer', color: 'var(--c-text-2)',
                                                                borderLeft: '2px solid ' + (STATUS_COLORS[pc.status]?.dot || '#9ca3af'),
                                                                marginBottom: 4, background: 'var(--c-msg-bg)',
                                                            }}>
                                                            <div style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                                                <span>{STATUS_LABEL[pc.status] || pc.status}</span>
                                                                <span style={{ color: 'var(--c-text-4)' }}>{relTime(pc.lastMessageAt || pc.openedAt)}</span>
                                                            </div>
                                                            <div style={{ color: 'var(--c-text-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {pc.lastMessage?.text || '(sin texto)'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ fontSize: 12, color: 'var(--c-text-4)' }}>—</div>
                                    )}
                                </div>

                                {/* Tickets DBA del cliente (Sprint UI/UX) */}
                                {clientCtx?.tickets?.length > 0 && (
                                    <div className="si-info-section">
                                        <h3>Tickets relacionados</h3>
                                        {clientCtx.tickets.slice(0, 8).map(t => (
                                            <Link key={t._id} to={`/tickets/${t._id}`}
                                                style={{
                                                    display: 'block', padding: '6px 8px', borderRadius: 6,
                                                    fontSize: 12, textDecoration: 'none', color: 'var(--c-text-2)',
                                                    background: 'var(--c-msg-bg)', marginBottom: 4,
                                                    borderLeft: '2px solid ' + (['closed','cancelled'].includes(t.status) ? '#9ca3af' : '#2563eb'),
                                                }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                                                    <span>#{t.folio}</span>
                                                    <span style={{ color: 'var(--c-text-4)', fontSize: 11 }}>{t.status}</span>
                                                </div>
                                                <div style={{ color: 'var(--c-text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {t.title}
                                                </div>
                                                {t.department?.name && (
                                                    <div style={{ color: 'var(--c-text-4)', fontSize: 11 }}>
                                                        {t.department.name}
                                                    </div>
                                                )}
                                            </Link>
                                        ))}
                                    </div>
                                )}

                                {active.client?.metadata && Object.keys(active.client.metadata).length > 0 && (
                                    <div className="si-info-section">
                                        <h3>Datos de cuenta</h3>
                                        <AccountDataPanel data={active.client.metadata} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}


// ── Componente de adjunto autenticado ────────────────────────────────────────
function SupportAttachment({ msgId, idx, att, mine }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    const wantsInline = isImage(att.mimeType) || isPdf(att.mimeType, att.name) || isAudio(att.mimeType, att.name);

    useEffect(() => {
        if (!wantsInline) return undefined;
        let cancelled = false; let url = null;
        setLoading(true);
        api.get(`/support/messages/${msgId}/attachments/${idx}`, { responseType: 'blob' })
            .then(res => { if (!cancelled) { url = URL.createObjectURL(res.data); setBlobUrl(url); } })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [msgId, idx, wantsInline]);

    const downloadClick = async () => {
        try {
            const res = await api.get(`/support/messages/${msgId}/attachments/${idx}`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.download = att.name || 'adjunto';
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (e) { toast.error('No se pudo descargar'); }
    };

    const linkColor = mine ? '#fff' : 'var(--brand-700, #0a4aa8)';

    if (isImage(att.mimeType)) {
        const qc = att.qualityCheck;
        const showRetake = qc && qc.retake;
        const showOk     = qc && qc.ok === true;
        const suspiciousKinds = new Set(['photo_of_screen', 'meme_or_sticker', 'cartoon', 'ai_generated', 'photo_of_paper_photo', 'watermark']);
        const kindLabels = {
            photo_of_screen:      'Foto de otra pantalla',
            meme_or_sticker:      'Meme / sticker',
            cartoon:              'Caricatura / dibujo',
            ai_generated:         'Generada por IA',
            photo_of_paper_photo: 'Foto de foto impresa',
            watermark:            'Marca de agua',
        };
        const isSuspicious = qc && suspiciousKinds.has(qc.subjectKind);
        return (
            <div style={{ marginTop: 6 }}>
                {loading || !blobUrl ? (
                    <div style={{ width: 160, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.05)', borderRadius: 8 }}>
                        <Spinner size="sm" animation="border" />
                    </div>
                ) : (
                    <a href={blobUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', position: 'relative' }}>
                        <img src={blobUrl} alt={att.name} style={{ maxWidth: 240, maxHeight: 180, borderRadius: 8, display: 'block' }} />
                        {showRetake && (
                            <span
                                title={qc.message || (qc.issues || []).join(', ')}
                                style={{
                                    position: 'absolute', top: 6, left: 6,
                                    background: '#dc2626', color: '#fff',
                                    padding: '3px 9px', borderRadius: 999,
                                    fontSize: 10, fontWeight: 700,
                                    letterSpacing: 0.2,
                                    boxShadow: '0 2px 6px rgba(0,0,0,.35)',
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                }}>
                                ⚠ Tomar nuevamente
                            </span>
                        )}
                        {!showRetake && isSuspicious && (
                            <span
                                title={qc.message || (qc.issues || []).join(', ')}
                                style={{
                                    position: 'absolute', top: 6, left: 6,
                                    background: '#d97706', color: '#fff',
                                    padding: '3px 9px', borderRadius: 999,
                                    fontSize: 10, fontWeight: 700,
                                    boxShadow: '0 2px 6px rgba(0,0,0,.35)',
                                }}>
                                ⚠ Sospechosa
                            </span>
                        )}
                        {!showRetake && !isSuspicious && showOk && (
                            <span
                                title="Calidad validada por IA"
                                style={{
                                    position: 'absolute', top: 6, right: 6,
                                    background: 'rgba(5, 150, 105, .92)', color: '#fff',
                                    padding: '2px 7px', borderRadius: 999,
                                    fontSize: 9, fontWeight: 700,
                                    boxShadow: '0 2px 6px rgba(0,0,0,.25)',
                                }}>
                                ✓ OK
                            </span>
                        )}
                    </a>
                )}
                {qc && (isSuspicious || (qc.issues || []).length > 0) && (
                    <p style={{
                        margin: '5px 0 0', fontSize: 11,
                        color: mine ? 'rgba(255,255,255,.85)' : 'var(--c-text-3)',
                        lineHeight: 1.4, fontStyle: 'italic', maxWidth: 240,
                    }}>
                        {isSuspicious ? `📌 ${kindLabels[qc.subjectKind]}. ` : ''}
                        {(qc.issues || []).filter(i => !suspiciousKinds.has(i)).join(', ')}
                    </p>
                )}
            </div>
        );
    }
    if (isAudio(att.mimeType, att.name)) {
        return (
            <div style={{ marginTop: 6 }}>
                {loading || !blobUrl ? (
                    <div style={{ width: 240, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.05)', borderRadius: 8 }}>
                        <Spinner size="sm" animation="border" />
                    </div>
                ) : (
                    <audio controls preload="metadata" src={blobUrl} style={{ display: 'block', maxWidth: 280 }} />
                )}
            </div>
        );
    }
    if (isPdf(att.mimeType, att.name)) {
        return (
            <div style={{ marginTop: 6 }}>
                <a href={blobUrl || '#'} target="_blank" rel="noreferrer"
                   style={{ color: linkColor, fontSize: 13, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Icon size={14}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></Icon>
                    {att.name}
                </a>
                {loading || !blobUrl ? (
                    <div style={{ width: 280, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.05)', borderRadius: 6 }}>
                        <Spinner size="sm" animation="border" />
                    </div>
                ) : (
                    <iframe title={att.name} src={blobUrl} style={{ width: 280, height: 200, border: '1px solid rgba(0,0,0,.1)', borderRadius: 6, background: '#fff' }} />
                )}
            </div>
        );
    }
    return (
        <div style={{ marginTop: 6 }}>
            <button type="button" onClick={downloadClick}
                style={{
                    background: 'none', border: 'none', padding: 0,
                    color: linkColor, fontSize: 13, textDecoration: 'underline',
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: 'inherit',
                }}>
                <Icon size={14}><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.58 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" /></Icon>
                {att.name}
            </button>
        </div>
    );
}
